<template>
  <div class="building-defects-report-display">
    <div class="card card-default mb-4">
      <h4
        class="card-header"
        v-html="title" />
      <div class="card-body">
        <h5 v-if="defectsReport.name">
          {{ defectsReport.propertyAddress }}
        </h5>
        <p class="text-muted" v-if="defectsReport.StaffMember">
          {{ defectsReport.StaffMember.fullName }} - <i class="far fa-clock fa-fw" /> {{ defectsReport.createdAt | date_time_format }}
          <span class="float-right" v-if="$user.administrator"><i class="fas fa-stopwatch fa-fw" /> Time spent: {{ timeSpent }}</span>
        </p>
        <template v-if="defectsReport.TaxInvoiceRequest">
          <p>
            <strong>
              Tax Invoice Request
            </strong>
            <br>
            <download-input
              :options="{ label: defectsReport.TaxInvoiceRequest.referenceNumber, permalink: 'downloadFile'}"
              :value="{ objectKey: defectsReport.TaxInvoiceRequest.InvoiceRequestPdf.objectKey }" />
          </p>
          <p v-if="defectsReport.TaxInvoiceRequest.UploadedInvoice">
            <strong>
              Uploaded Invoice
            </strong>
            <br>
            <download-input
              :options="{ label: defectsReport.TaxInvoiceRequest.UploadedInvoice.fileName, permalink: 'downloadFile'}"
              :value="{ objectKey: defectsReport.TaxInvoiceRequest.UploadedInvoice.objectKey }" />
          </p>
        </template>
        <p v-if="defectsReport.ScottSchedule">
          <strong>
            Scott Schedule 
          </strong>
          <br/>
          <download-input
              :options="{ permalink: 'downloadFile'}"
              :value="{ objectKey: defectsReport.ScottSchedule.objectKey }" />
        </p>
        <template v-if="defectsReport.SentEmails.length">
          <sent-email
            v-for="email in defectsReport.SentEmails"
            :key="email.uuid"
            :email="email">
          </sent-email>
        </template>
      </div>
      <div class="card-footer">
        <router-link
          :to="{ path: 'edit/' + defectsReport.uuid, append: true }"
          class="my-1 btn btn-black">
          <i class="far fa-fw fa-edit" /> Edit
        </router-link>
        <router-link
          target="_blank"
          :to="{ path: '/api/public/pdf/' + defectsReport.uuid + '?m=DefectsReport' }"
          class="my-1 btn btn-secondary"
          :class="{ 'disabled': !defectsReport.ReportPdfUuid }">
          <i class="fa fa-fw fa-search" /> Preview
        </router-link>
        <download-input
          :disabled="!defectsReport.ReportPdfUuid"
          :options="{ button: true, permalink: 'downloadFile'}"
          :value="{ objectKey: defectsReport.ReportPdf ? defectsReport.ReportPdf.objectKey : '' }" />
        <button 
          class="my-1 btn btn-primary" 
          @click="generatePdf()">
          <i class="fa fa-fw fa-redo-alt" /> Generate PDF
        </button>
        <button 
          class="my-1 btn btn-info"
          @click="upload()">
          <i class="fa fa-fw fa-upload" /> Upload
        </button>
        <button 
          class="my-1 btn btn-success" 
          @click="send()" 
          :disabled="!defectsReport.complete">
          <i class="far fa-fw fa-paper-plane" /> Send
        </button>
        <button 
          class="my-1 btn btn-lime" 
          @click="duplicate()">
          <i class="far fa-fw fa-copy" /> Duplicate
        </button>
        <router-link 
          :to="'/tax-invoice-requests/new/' + defectsReport.uuid + '?t=DefectsReport'"
          class="my-1 btn btn-warning"
          :class="{ 'disabled': !generateInvoiceButton }">
          <i class="fa fa-fw fa-dollar-sign" /> Invoice Request
        </router-link>
        <router-link 
          :class="{ 'disabled': !defectsReport.complete }"
          :to="{ path: 'scott-schedule/' + defectsReport.uuid, append: true }"
          class="my-1 btn btn-pink">
          <i class="fa fa-fw fa-table" /> Scott Schedule
        </router-link>
        <button 
          @click="deleteDefectsReport()" 
          class="my-1 btn btn-danger">
          <i class="fa fa-fw fa-trash" /> Delete
        </button>
      </div>
    </div>
  </div>
</template>
<script>
  import {
    DELETE_REPORT,
    DUPLICATE_DEFECTS_REPORT,
    GENERATE_DEFECTS_PDF,
    SEND_DEFECTS_REPORT,
    UPLOAD_DEFECTS_PDF
  } from '@/vuex/types'
  import SentEmail from '@/components/displays/SentEmail'
  import DownloadInput from '@/components/inputs/DownloadInput'
  export default {
    name: 'DefectsReportDisplay',
    props: {
      object: {
        type: Object,
        default () {
          return {}
        }
      }
    },
    components: {
      SentEmail,
      DownloadInput
    },
    computed: {
      timeSpent () {
        let minutes = this.defectsReport.time / 60.0
        if (minutes > 120) {
          let hours = minutes / 60
          return hours.toFixed(2) + ' hours'
        } else {
          return minutes.toFixed(2) + ' min'
        }
      },
      fileName () {
        if (this.defectsReport.ReportPdf) {
          return this.defectsReport.ReportPdf.fileName.slice(0, -4)
        } else {
          return this.defectsReport.propertyAddress
        }
      },
      generateInvoiceButton () {
        return this.defectsReport.complete && !this.defectsReport.TaxInvoiceRequestUuid
      },
      title () {
        if (this.defectsReport.name) {
          return '<i class="fa fa-fw fa-file"></i> ' + this.defectsReport.name
        } else if (this.defectsReport.propertyAddress) {
          return '<i class="fa fa-fw fa-map-marker-alt"></i> ' + this.defectsReport.propertyAddress
        } else {
          return '<i class="fa fa-fw fa-certificate"></i> New Report'
        }
      },
      defectsReport () {
        return {
          StaffMember: {},
          ...this.object
        }
      }
    },
    methods: {
      async deleteDefectsReport () {
        await this.$store.dispatch(DELETE_REPORT, {
          reportUuid: this.defectsReport.uuid,
          reportType: 'DefectsReport'
        })
      },
      async generatePdf () {
        await this.$store.dispatch(GENERATE_DEFECTS_PDF, this.defectsReport)
      },
      async duplicate () {
        await this.$store.dispatch(DUPLICATE_DEFECTS_REPORT, this.defectsReport.uuid)
      },
      async upload () {
        await this.$store.dispatch(UPLOAD_DEFECTS_PDF, this.defectsReport.uuid)
      },
      async send () {
        await this.$store.dispatch(SEND_DEFECTS_REPORT, this.defectsReport.uuid)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/cards/DefectsReport.vue